import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, ArrowUpDown, Filter, Loader2 } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import {
  createQuestion,
  deleteQuestion,
  updateQuestion,
  getQuestions,
  getQuestionById,
} from "../../services/questions";
import {
  EmptyResponse,
  ErrorResponse,
  Question,
  QuestionsMode,
  QuestionType,
  UserRole,
} from "../../types";
import Notification, { NotificationAction } from "../Notification";
import Header from "../Header";
import { QuestionForm } from "./QuestionForm";
import QuestionsList from "./QuestionsList";

interface QuestionsPageProps {
  mode: QuestionsMode;
}

export default function QuestionsPage({ mode }: QuestionsPageProps) {
  const { language, translations, token, user, role } = useAuth();
  const queryClient = useQueryClient();
  const audioRefs = useRef<Record<string, HTMLAudioElement | null>>({});
  const isGeneric = mode.kind === "generic";
  const lockedForOrganization = role === UserRole.Organization && isGeneric;
  // Títulos/leyendas según modo
  const labels = useMemo(() => {
    const isGeneric = mode.kind === "generic";
    return {
      title: isGeneric
        ? translations.genericQuestions
        : translations.specificQuestions,
      subtitle: isGeneric
        ? translations.manageGenericQuestions
        : translations.manageSpecificQuestions,
      infoTitle: isGeneric
        ? translations.aboutGenericQuestions
        : translations.aboutSpecificQuestions,
      infoBullets: isGeneric
        ? [
            translations.genericQuestionsAvailable,
            translations.organizationsCanAdd,
            translations.onlyAdminsCanCreateGeneric,
            translations.eachQuestionMustHaveTranslations,
            translations.canAddAudioForAccessibility,
          ]
        : [
            translations.specificQuestionsArePerSurvey,
            translations.onlyOwnersOrAdminsCanEditSpecific,
            translations.eachQuestionMustHaveTranslations,
            translations.canAddAudioForAccessibility,
          ],
    };
  }, [mode, translations]);

  const queryKey =
    mode.kind === "generic"
      ? ["questions", "generic"]
      : ["questions", "specific"];

  const fetcher = async () => {
    if (!token) return [] as Question[];
    if (mode.kind === "generic") {
      return getQuestions(token, { isGeneric: true, includeAudioFiles: false });
    }
    if (user?.role === "Administrator") {
      return getQuestions(token, { isGeneric: false, includeAudioFiles: false });
    }
    const allSpecific = await getQuestions(token, { isGeneric: false, includeAudioFiles: false });
    return allSpecific.filter((q) => q.organizationId === user?.organizationId);
  };

  const { data, isLoading, error, refetch } = useQuery<Question[]>({
    queryKey,
    queryFn: fetcher,
    enabled: !!token,
    refetchOnMount: true,
  });

  const [list, setList] = useState<Question[]>([]);
  useEffect(() => {
    setList(data || []);
  }, [data]);

  const [currentPlayingId, setCurrentPlayingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [loadingQuestion, setLoadingQuestion] = useState(false);

  // Refrescar datos cuando se cierra el formulario
  useEffect(() => {
    if (!showForm && token) {
      // Pequeño delay para asegurar que el servidor ha procesado los cambios
      const timeoutId = setTimeout(() => {
        refetch();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [showForm, token, refetch]);
  const [notification, setNotification] = useState<{
    type: "success" | "error" | "warning" | "info";
    title: string;
    message?: string;
    actions?: NotificationAction[];
  } | null>(null);

  // ---- Filtro por organización (solo en "specific" y Admin) ----
  const isSpecificAdmin =
    mode.kind === "specific" && user?.role === "Administrator";
  const [orgFilter, setOrgFilter] = useState<string>("all");

  const orgOptions = useMemo(() => {
    if (!isSpecificAdmin) return [];
    const seen = new Map<string, string>();
    for (const q of list) {
      if (q.organizationId) {
        const name =
          (q as any).organizationName ||
          (q as any).organization?.name ||
          q.organizationId;
        if (!seen.has(q.organizationId)) seen.set(q.organizationId, name);
      }
    }
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [isSpecificAdmin, list]);

  // ---- Search state ----
  const [searchQuery, setSearchQuery] = useState<string>("");

  // ---- Sort state ----
  type SortOrder = "newest" | "oldest" | "default";
  const [sortOrder, setSortOrder] = useState<SortOrder>("default");

  // ---- Audio filter state ----
  type AudioFilter = "all" | "withAudio" | "withoutAudio";
  const [audioFilter, setAudioFilter] = useState<AudioFilter>("all");

  // Helper function to check if question has audio
  const hasAudio = useMemo(() => {
    return (q: Question): boolean => {
      const langs: Array<"es" | "ca" | "en" | "ar"> = ["es", "ca", "en", "ar"];
      return langs.some((lang) => {
        // Check audioFiles (local files)
        if (q.audioFiles?.[lang]) return true;
        
        // Check audioUrls (can be blob URLs or API URLs like "voice/u0zeg10w.mp3")
        const audioUrl = q.audioUrls?.[lang];
        if (audioUrl && audioUrl.trim() && audioUrl.trim() !== "") {
          return true;
        }
        
        // Check audioObsKeys (API URLs like "voice/u0zeg10w.mp3")
        const audioObsKey = q.audioObsKeys?.[lang];
        if (audioObsKey && audioObsKey.trim() && audioObsKey.trim() !== "") {
          return true;
        }
        
        return false;
      });
    };
  }, []);

  const filteredList = useMemo(() => {
    let result = list;

    // Apply organization filter first
    if (isSpecificAdmin && orgFilter !== "all") {
      result = result.filter((q) => q.organizationId === orgFilter);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((q) => {
        const texts = [
          q.text.es,
          q.text.ca,
          q.text.en,
          q.text.ar,
        ].filter(Boolean);
        return texts.some((text) =>
          text.toLowerCase().includes(query)
        );
      });
    }

    // Apply audio filter
    if (audioFilter !== "all") {
      result = result.filter((q) => {
        const hasAnyAudio = hasAudio(q);
        return audioFilter === "withAudio" ? hasAnyAudio : !hasAnyAudio;
      });
    }

    // Apply sort
    if (sortOrder !== "default") {
      result = [...result].sort((a, b) => {
        const dateA = a.createdAt
          ? new Date(a.createdAt).getTime()
          : Number.NEGATIVE_INFINITY; // Put items without date at the end
        const dateB = b.createdAt
          ? new Date(b.createdAt).getTime()
          : Number.NEGATIVE_INFINITY;

        if (sortOrder === "newest") {
          return dateB - dateA; // Newest first
        } else {
          return dateA - dateB; // Oldest first
        }
      });
    }

    return result;
  }, [isSpecificAdmin, orgFilter, list, searchQuery, audioFilter, sortOrder, hasAudio]);

  const handleSaveQuestion = async (questionData: Partial<Question>): Promise<void> => {
    let response: Question | ErrorResponse | EmptyResponse;

    if (editingQuestion) {
      const updated: Omit<Question, "createdAt"> = {
        ...editingQuestion,
        ...questionData,
        type:
          mode.kind === "generic" ? QuestionType.Generic : QuestionType.Custom,
        isGeneric: mode.kind === "generic",
        organizationId:
          mode.kind === "generic"
            ? undefined
            : questionData.organizationId ?? user?.organizationId,
      };
      response = await updateQuestion(updated.id, updated, token!);
    } else {
      const newQ: Question = {
        id: crypto.randomUUID?.() ?? Date.now().toString(),
        text: questionData.text || { es: "", ca: "" },
        type:
          mode.kind === "generic" ? QuestionType.Generic : QuestionType.Custom,
        order: (list?.length ?? 0) + 1,
        isGeneric: mode.kind === "generic",
        audioUrl: questionData.audioUrl,
        audioUrls: questionData.audioUrls || { es: "", ca: "" },
        organizationId:
          mode.kind === "generic"
            ? undefined
            : questionData.organizationId ?? user?.organizationId,
      };
      response = await createQuestion(newQ, token!);
    }

    if ("error" in response) {
      setNotification({
        type: "error",
        title: `${translations.errorCreatingUpdating} ${
          editingQuestion ? translations.updating : translations.creating
        } ${translations.question}`,
        message: response.message,
      });
      throw new Error(response.message);
    }

    // Invalidar y refetch para asegurar que los datos se actualicen
    await queryClient.invalidateQueries({ queryKey });
    await refetch();

    // Cerrar formulario y volver al listado después de actualizar datos
    setShowForm(false);
    setEditingQuestion(null);

    setNotification({
      type: "success",
      title: `${translations.question} ${
        editingQuestion ? translations.updated : translations.created
      } ${translations.correctly}`,
    });
  };

  const handleEditQuestion = async (q: Question) => {
    if (!token) return;
    
    // Cargar la pregunta completa con archivos de audio antes de mostrar el formulario
    setLoadingQuestion(true);
    
    try {
      const fullQuestion = await getQuestionById(q.id, token, true);
      if ("error" in fullQuestion) {
        setNotification({
          type: "error",
          title: translations.error ?? "Error",
          message: fullQuestion.message ?? "Error al cargar la pregunta completa",
        });
        setLoadingQuestion(false);
        return;
      }
      // Solo mostrar el formulario cuando tengamos la pregunta completa
      setEditingQuestion(fullQuestion);
      setShowForm(true);
    } catch (error) {
      console.error("Error loading question:", error);
      setNotification({
        type: "error",
        title: translations.error ?? "Error",
        message: "Error al cargar los archivos de audio de la pregunta",
      });
    } finally {
      setLoadingQuestion(false);
    }
  };

  const moveQuestion = async (questionId: string, direction: "up" | "down") => {
    const idx = list.findIndex((q) => q.id === questionId);
    if (idx < 0) return;
    if (
      (direction === "up" && idx === 0) ||
      (direction === "down" && idx === list.length - 1)
    )
      return;

    const targetIdx = direction === "up" ? idx - 1 : idx + 1;
    const newList = [...list];
    [newList[idx], newList[targetIdx]] = [newList[targetIdx], newList[idx]];

    const withOrder = newList.map((q, i) => ({ ...q, order: i + 1 }));
    setList(withOrder);

    try {
      const a = withOrder[idx];
      const b = withOrder[targetIdx];
      await Promise.all([
        updateQuestion(a.id, a, token!),
        updateQuestion(b.id, b, token!),
      ]);
      queryClient.invalidateQueries({ queryKey });
    } catch (e) {
      setNotification({
        type: "error",
        title: translations.errorReordering ?? "Error reordenando",
      });
    }
  };

  const handleDeleteQuestion = async (questionId: string) => {
    const response = await deleteQuestion(questionId, token!);
    if ("error" in response) {
      setNotification({
        type: "error",
        title: translations.errorDeletingSurvey,
        message: response.message,
      });
      return response;
    }
    await queryClient.invalidateQueries({ queryKey });
    setNotification({
      type: "success",
      title: translations.surveyDeletedSuccessfully,
    });
    return response;
  };

  const notificationComponent = notification && (
    <div className="fixed top-6 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md">
      <Notification
        type={notification.type}
        title={notification.title}
        actions={notification.actions}
        message={notification.message}
        duration={notification.actions?.length ? 0 : 5000}
        onClose={() => setNotification(null)}
      />
    </div>
  );

  if (loadingQuestion) {
    return (
      <>
        {notificationComponent}
        <div className="p-6 space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 flex flex-col items-center justify-center">
            <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
            <p className="mt-4 text-gray-600">
              {translations.loadingQuestion ?? "Cargando pregunta..."}
            </p>
          </div>
        </div>
      </>
    );
  }

  if (showForm) {
    return (
      <>
        {notificationComponent}
        <QuestionForm
          questionKind={mode.kind}
          setNotification={setNotification}
          question={editingQuestion}
          onSave={handleSaveQuestion}
          onCancel={() => {
            setShowForm(false);
            setEditingQuestion(null);
          }}
          mode={editingQuestion ? "edit" : "create"}
        />
      </>
    );
  }

  return (
    <>
      {notificationComponent}
      <div className="p-6 space-y-6">
        <Header>
          <div className="flex items-center justify-between w-full">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {labels.title}
              </h1>
              <p className="text-gray-600 mt-1">{labels.subtitle}</p>
            </div>
          </div>
        </Header>

        {/* Search and Filters Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6">
            <div className="flex items-center gap-4 flex-wrap">
              {/* Add Question Button */}
              {!lockedForOrganization && (
                <button
                  onClick={() => setShowForm(true)}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap flex-shrink-0"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Añadir
                </button>
              )}
              
              {/* Organization Filter (for admin in specific mode) */}
              {isSpecificAdmin && (
                <div className="flex items-center gap-2 flex-shrink-0">
                  <label htmlFor="orgFilter" className="text-sm text-gray-700 whitespace-nowrap">
                    {translations.organization ?? "Organización"}:
                  </label>
                  <select
                    id="orgFilter"
                    value={orgFilter}
                    onChange={(e) => setOrgFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm min-w-[150px]"
                  >
                    <option value="all">
                      {translations.allOrganizations ?? "Todas"}
                    </option>
                    {orgOptions.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Search - takes available space */}
              <div className="flex-1 min-w-[200px] relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={translations.searchQuestions ?? "Buscar preguntas..."}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Sort */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <ArrowUpDown className="w-4 h-4 text-gray-400" />
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value as SortOrder)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[180px]"
                >
                  <option value="default">
                    {translations.defaultOrder ?? "Orden por defecto"}
                  </option>
                  <option value="newest">
                    {translations.newestFirst ?? "Más recientes primero"}
                  </option>
                  <option value="oldest">
                    {translations.oldestFirst ?? "Más antiguas primero"}
                  </option>
                </select>
              </div>

              {/* Audio Filter */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <Filter className="w-4 h-4 text-gray-400" />
                <select
                  value={audioFilter}
                  onChange={(e) => setAudioFilter(e.target.value as AudioFilter)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[160px]"
                >
                  <option value="all">
                    {translations.allQuestions ?? "Todas las preguntas"}
                  </option>
                  <option value="withAudio">
                    {translations.questionsWithAudio ?? "Con audio"}
                  </option>
                  <option value="withoutAudio">
                    {translations.questionsWithoutAudio ?? "Sin audio"}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <QuestionsList
          isLoading={isLoading}
          error={error}
          mode={mode}
          questions={filteredList}
          language={language}
          currentPlayingId={currentPlayingId}
          setCurrentPlayingId={setCurrentPlayingId}
          audioRefs={audioRefs}
          handleEditQuestion={handleEditQuestion}
          handleDeleteQuestion={handleDeleteQuestion}
          moveQuestion={moveQuestion}
          setNotification={setNotification}
          setShowForm={setShowForm}
        />

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <div className="w-5 h-5 text-blue-400">ℹ️</div>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                {labels.infoTitle}
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <ul className="list-disc list-inside space-y-1">
                  {labels.infoBullets.map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
        {error && (
          <p className="text-sm text-red-600">
            {translations.errorLoadingQuestions}
          </p>
        )}
      </div>
    </>
  );
}
